import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Avatar,
  Snackbar,
  Alert,
  Divider,
  Switch,
  List,
  ListItem,
  Chip,
  IconButton,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CakeIcon from '@mui/icons-material/Cake';
import CampaignIcon from '@mui/icons-material/Campaign';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import SecurityIcon from '@mui/icons-material/Security';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/ConfigContext';
import { getInitials, isVolunteerOnly } from '../../utils/helpers';
import { computeFamilySize } from '../../utils/familyMembers';
import { FamilyMember, RELATIONSHIP_LABELS, Relationship } from '../../types';
import { ROUTES } from '../../utils/constants';
import api from '../../services/api';
import PushNotificationPrompt from '../../components/PushNotificationPrompt';
import {
  MEMBER_PROFILE_FIELDS,
  MemberProfileField,
  normaliseProfileValue,
  profileFieldChanged,
} from '../../utils/memberProfile';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

const ProfilePage: React.FC = () => {
  const { user, updateUser, refreshUser } = useAuth();
  const { features } = useConfig();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [savingPhotoPermission, setSavingPhotoPermission] = useState(false);
  const [savingNameBirthdayPermission, setSavingNameBirthdayPermission] = useState(false);
  const [savingNewsEventsPermission, setSavingNewsEventsPermission] = useState(false);
  const [savingOnlineActivitiesPermission, setSavingOnlineActivitiesPermission] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Editable per-member personal-profile drafts (Interests / Favourite Food &
  // Drinks / Allergies / Dietary), keyed by `${memberId}:${field}`, plus the
  // in-flight key. Generalises the C7 dietary save-on-blur to all four fields.
  const [profileDrafts, setProfileDrafts] = useState<Record<string, string>>({});
  const [savingProfileKey, setSavingProfileKey] = useState<string | null>(null);
  // Phase 0 — per-member photo permission toggle in-flight id.
  const [savingPhotoMemberId, setSavingPhotoMemberId] = useState<string | null>(null);

  // Phase 0 — toggle a specific family member's photo permission. Pushes to CRM
  // via the existing per-member endpoint (Plan 4C Gap 16).
  const handleToggleMemberPhoto = async (member: FamilyMember) => {
    const next = !member.photoPermission;
    setSavingPhotoMemberId(member.id);
    setSaveError(null);
    try {
      await api.patch(`/users/family-members/${member.id}/photo-permission`, {
        photoPermission: next,
      });
      setFamilyMembers((prev) =>
        prev.map((m) => (m.id === member.id ? { ...m, photoPermission: next } : m)),
      );
      setShowSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Couldn't update photo permission.";
      setSaveError(message);
    } finally {
      setSavingPhotoMemberId(null);
    }
  };

  // Persist a member's personal-profile field when it loses focus and changed.
  // Writes the App DB and pushes to the CRM via the two-way /profile endpoint.
  const handleSaveProfileField = async (member: FamilyMember, field: MemberProfileField) => {
    const key = `${member.id}:${field}`;
    const draft = profileDrafts[key] ?? '';
    if (!profileFieldChanged(member[field] as string | null | undefined, draft)) return; // unchanged
    const value = normaliseProfileValue(draft);
    setSavingProfileKey(key);
    setSaveError(null);
    try {
      await api.patch(`/users/family-members/${member.id}/profile`, {
        [field]: value,
      });
      setFamilyMembers((prev) =>
        prev.map((m) => (m.id === member.id ? { ...m, [field]: value } : m)),
      );
      setShowSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Couldn't save.";
      setSaveError(message);
    } finally {
      setSavingProfileKey(null);
    }
  };

  const handleRefreshPermissions = async () => {
    setRefreshing(true);
    setSaveError(null);
    try {
      await refreshUser();
      setShowSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || "Couldn't refresh permissions. Try again.";
      setSaveError(message);
    } finally {
      setRefreshing(false);
    }
  };

  // Load family members from user profile
  useEffect(() => {
    if (user?.familyMembers) {
      setFamilyMembers(user.familyMembers);
    } else if (user?.children) {
      // Backward compatibility - convert children to family members
      setFamilyMembers(user.children.map(c => ({
        ...c,
        relationship: 'child' as Relationship,
      })));
    }
  }, [user]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      phone: user?.phone || '',
    },
  });

  const onSubmit = async (data: ProfileFormData) => {
    if (!user) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const response = await api.put('/users/profile', {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone,
      });

      // Update user context with response data
      updateUser({
        ...user,
        ...response.data,
      });

      setIsEditing(false);
      setShowSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to save profile';
      setSaveError(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTogglePhotoPermission = async () => {
    setSavingPhotoPermission(true);
    setSaveError(null);

    try {
      const newPermission = !user?.photoPermission;
      await api.patch('/users/profile/photo-permission', {
        photoPermission: newPermission,
      });

      // Update user context
      if (user) {
        updateUser({
          ...user,
          photoPermission: newPermission,
        });
      }

      setShowSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to update photo permission';
      setSaveError(message);
    } finally {
      setSavingPhotoPermission(false);
    }
  };

  const handleToggleNameBirthdayPermission = async () => {
    setSavingNameBirthdayPermission(true);
    setSaveError(null);

    try {
      const newPermission = !user?.nameAndBirthdayPermission;
      await api.patch('/users/profile/name-birthday-permission', {
        nameAndBirthdayPermission: newPermission,
      });

      // Update user context
      if (user) {
        updateUser({
          ...user,
          nameAndBirthdayPermission: newPermission,
        });
      }

      setShowSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to update permission';
      setSaveError(message);
    } finally {
      setSavingNameBirthdayPermission(false);
    }
  };

  const handleToggleNewsEventsPermission = async () => {
    setSavingNewsEventsPermission(true);
    setSaveError(null);

    try {
      const newPermission = !user?.newsAndEventsPermission;
      await api.patch('/users/profile/news-events-permission', {
        newsAndEventsPermission: newPermission,
      });

      // Update user context
      if (user) {
        updateUser({
          ...user,
          newsAndEventsPermission: newPermission,
        });
      }

      setShowSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to update permission';
      setSaveError(message);
    } finally {
      setSavingNewsEventsPermission(false);
    }
  };

  const handleToggleOnlineActivitiesPermission = async () => {
    setSavingOnlineActivitiesPermission(true);
    setSaveError(null);

    try {
      const newPermission = !user?.onlineActivitiesPermission;
      await api.patch('/users/profile/online-activities-permission', {
        onlineActivitiesPermission: newPermission,
      });

      // Update user context
      if (user) {
        updateUser({
          ...user,
          onlineActivitiesPermission: newPermission,
        });
      }

      setShowSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.error || err?.message || 'Failed to update permission';
      setSaveError(message);
    } finally {
      setSavingOnlineActivitiesPermission(false);
    }
  };

  const handleCancel = () => {
    reset();
    setIsEditing(false);
  };

  if (!user) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5">Please log in to view your profile</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        {isVolunteerOnly(user) ? 'Volunteer Profile' : 'Profile'}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your account information
      </Typography>

      <Grid container spacing={4}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', p: 3 }}>
            <Avatar
              sx={{
                width: 100,
                height: 100,
                mx: 'auto',
                mb: 2,
                backgroundColor: '#00b7ed',
                fontSize: '2rem',
              }}
            >
              {getInitials(user.firstName, user.lastName)}
            </Avatar>
            <Typography variant="h5" fontWeight={600}>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'inline-block',
                  px: 2,
                  py: 0.5,
                  backgroundColor: isVolunteerOnly(user)
                    ? '#4caf50'
                    : user.role === 'admin'
                      ? '#9c27b0'
                      : user.role === 'angel'
                        ? '#7e57c2'
                        : '#00b7ed',
                  color: 'white',
                  borderRadius: 1,
                  textTransform: 'capitalize',
                }}
              >
                {isVolunteerOnly(user) ? 'Volunteer' : user.role === 'angel' ? 'Angel' : user.role} Account
              </Typography>
              {!isVolunteerOnly(user) && user.isVolunteer && user.volunteerStatus === 'approved' && (
                <Typography
                  variant="caption"
                  sx={{
                    display: 'inline-block',
                    px: 2,
                    py: 0.5,
                    backgroundColor: '#4caf50',
                    color: 'white',
                    borderRadius: 1,
                  }}
                >
                  Volunteer
                </Typography>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Profile Form */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon />
                  <Typography variant="h6" fontWeight={600}>
                    Personal Information
                  </Typography>
                </Box>
                {!isEditing && (
                  <Button variant="outlined" onClick={() => setIsEditing(true)}>
                    Edit
                  </Button>
                )}
              </Box>

              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="First Name"
                      disabled={!isEditing}
                      {...register('firstName', { required: 'First name is required' })}
                      error={!!errors.firstName}
                      helperText={errors.firstName?.message}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Last Name"
                      disabled={!isEditing}
                      {...register('lastName', { required: 'Last name is required' })}
                      error={!!errors.lastName}
                      helperText={errors.lastName?.message}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      disabled
                      {...register('email')}
                      helperText="Email cannot be changed"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      disabled={!isEditing || isSaving}
                      {...register('phone')}
                      helperText={isEditing ? 'Changes will sync to Team Jak CRM' : undefined}
                    />
                  </Grid>
                  {!isVolunteerOnly(user) && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Family Size"
                        disabled
                        // Counts living members + angels (a child who has passed
                        // is still part of the family) + the PC when not listed.
                        value={computeFamilySize(user)}
                        helperText="Total members including you"
                        InputProps={{ inputProps: { min: 1 } }}
                      />
                    </Grid>
                  )}
                </Grid>

                {isEditing && (
                  <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={!isDirty || isSaving}
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
                      Cancel
                    </Button>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h6" fontWeight={600} gutterBottom>
                Account Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Account created: {new Date(user.createdAt).toLocaleDateString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Account type: {isVolunteerOnly(user) ? 'Volunteer' : user.role === 'admin' ? 'Administrator' : user.role === 'angel' ? 'Angel Family' : user.role === 'warrior' ? 'Warrior Family' : 'Family'}
                </Typography>
                {user.isVolunteer && user.volunteerStatus === 'approved' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <VolunteerActivismIcon sx={{ fontSize: 18, color: '#4caf50' }} />
                    <Typography variant="body2" color="text.secondary">
                      Volunteer since {user.volunteerSince ? new Date(user.volunteerSince).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>
                )}
                {user.volunteerStatus === 'pending' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <VolunteerActivismIcon sx={{ fontSize: 18, color: '#ff9800' }} />
                    <Typography variant="body2" color="text.secondary">
                      Volunteer application pending
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Privacy & Permissions Section - Consolidated */}
        {(user.role === 'family' || user.role === 'warrior' || user.role === 'angel') && !isVolunteerOnly(user) && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SecurityIcon sx={{ color: '#00b7ed' }} />
                    <Typography variant="h6" fontWeight={600}>
                      Privacy & Permissions
                    </Typography>
                  </Box>
                  <IconButton
                    onClick={handleRefreshPermissions}
                    aria-label="Refresh permissions"
                    title="Refresh permissions from server"
                    disabled={refreshing}
                    size="small"
                    sx={{ flexShrink: 0 }}
                  >
                    <RefreshIcon
                      sx={refreshing ? {
                        animation: 'spin 1s linear infinite',
                        '@keyframes spin': {
                          '0%': { transform: 'rotate(0deg)' },
                          '100%': { transform: 'rotate(360deg)' },
                        },
                      } : undefined}
                    />
                  </IconButton>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Manage how Team Jak uses your information
                </Typography>

                {/* Photo Permission */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: '#f9f9f9',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <PhotoCameraIcon
                      sx={{
                        fontSize: 24,
                        color: user.photoPermission ? '#4caf50' : '#9e9e9e',
                      }}
                    />
                    <Box>
                      <Typography fontWeight={500}>Photo Permission</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Allow photos of your family in promotional materials
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={user.photoPermission || false}
                    onChange={handleTogglePhotoPermission}
                    color="success"
                    disabled={savingPhotoPermission}
                  />
                </Box>

                {/* Name & Birthday Permission */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: '#f9f9f9',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CakeIcon
                      sx={{
                        fontSize: 24,
                        color: user.nameAndBirthdayPermission ? '#4caf50' : '#9e9e9e',
                      }}
                    />
                    <Box>
                      <Typography fontWeight={500}>Child Name & Birthday Display</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Allow display of your child's name and birthday
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={user.nameAndBirthdayPermission || false}
                    onChange={handleToggleNameBirthdayPermission}
                    color="success"
                    disabled={savingNameBirthdayPermission}
                  />
                </Box>

                {/* News & Events Permission */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: '#f9f9f9',
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CampaignIcon
                      sx={{
                        fontSize: 24,
                        color: user.newsAndEventsPermission ? '#4caf50' : '#9e9e9e',
                      }}
                    />
                    <Box>
                      <Typography fontWeight={500}>News & Event Updates</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Receive news and information about Team Jak events
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={user.newsAndEventsPermission || false}
                    onChange={handleToggleNewsEventsPermission}
                    color="success"
                    disabled={savingNewsEventsPermission}
                  />
                </Box>

                {/* Online Activities & Screenshots Permission */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: '#f9f9f9',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ScreenshotMonitorIcon
                      sx={{
                        fontSize: 24,
                        color: user.onlineActivitiesPermission ? '#4caf50' : '#9e9e9e',
                      }}
                    />
                    <Box>
                      <Typography fontWeight={500}>Online Activities & Screenshots</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Allow screenshots/recordings from online activities
                      </Typography>
                    </Box>
                  </Box>
                  <Switch
                    checked={user.onlineActivitiesPermission || false}
                    onChange={handleToggleOnlineActivitiesPermission}
                    color="success"
                    disabled={savingOnlineActivitiesPermission}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Family Members Section - Show for family and warrior accounts */}
        {(user.role === 'family' || user.role === 'warrior' || user.role === 'angel') && familyMembers.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <GroupIcon sx={{ color: '#00b7ed' }} />
                  <Typography variant="h6" fontWeight={600}>
                    Family Members
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {features.selfAddMembers
                    ? "Add or remove your own family members using the button below. You can set each person's dietary requirements (for event catering) and photo permission here — changes are saved to Team Jak straight away."
                    : "Family members are managed through Team Jak CRM. Please contact Team Jak if your family details need updating. You can set each person's dietary requirements (for event catering) and photo permission below — changes are saved to Team Jak straight away."}
                </Typography>

                <List sx={{ bgcolor: '#f9f9f9', borderRadius: 2 }}>
                  {familyMembers.map((member, index) => (
                    <ListItem
                      key={member.id}
                      sx={{
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 1,
                        borderBottom:
                          index < familyMembers.length - 1 ? '1px solid #eee' : 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={500}>{member.name}</Typography>
                        <Chip
                          label={RELATIONSHIP_LABELS[member.relationship] || member.relationship}
                          size="small"
                          sx={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
                        />
                      </Box>
                      {/* Personal-profile fields, each saved on blur (two-way CRM sync). */}
                      {MEMBER_PROFILE_FIELDS.map(({ field, label, placeholder }) => {
                        const key = `${member.id}:${field}`;
                        return (
                          <TextField
                            key={key}
                            fullWidth
                            size="small"
                            label={label}
                            placeholder={placeholder}
                            value={profileDrafts[key] ?? (member[field] as string | null | undefined) ?? ''}
                            disabled={savingProfileKey === key}
                            inputProps={{ maxLength: 500 }}
                            onChange={(e) =>
                              setProfileDrafts((prev) => ({ ...prev, [key]: e.target.value }))
                            }
                            onBlur={() => handleSaveProfileField(member, field)}
                          />
                        );
                      })}
                      {/* Phase 0 — editable per-member photo permission. */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Allow Team Jak to photograph {member.name} at events
                        </Typography>
                        <Switch
                          checked={member.photoPermission || false}
                          onChange={() => handleToggleMemberPhoto(member)}
                          color="success"
                          disabled={savingPhotoMemberId === member.id}
                          inputProps={{ 'aria-label': `Photo permission for ${member.name}` }}
                        />
                      </Box>
                    </ListItem>
                  ))}
                </List>

                {/* D8 — manage self-added members (gated on the runtime flag). */}
                {features.selfAddMembers && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      component={RouterLink}
                      to={ROUTES.FAMILY_MEMBERS}
                      variant="outlined"
                      startIcon={<GroupIcon />}
                    >
                      Add or manage family members
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Plan 4.12 — Link to My Guests page (family + warrior + angel roles) */}
        {(user.role === 'family' || user.role === 'warrior' || user.role === 'angel') && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PeopleOutlineIcon sx={{ color: '#00b7ed' }} />
                  <Typography variant="h6" fontWeight={600}>
                    My Guests
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Save people you'd like to bring to Team Jak events alongside
                  your family — grandparents, aunts and uncles, friends. You can
                  manage and remove them at any time.
                </Typography>
                <Button
                  component={RouterLink}
                  to={ROUTES.MY_GUESTS}
                  variant="outlined"
                  startIcon={<PeopleOutlineIcon />}
                >
                  Manage my guests
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <PushNotificationPrompt />

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Updated successfully
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!saveError}
        autoHideDuration={6000}
        onClose={() => setSaveError(null)}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {saveError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfilePage;
